import {
  NextApiRequest,
  NextApiResponse,
  GetServerSidePropsContext,
} from 'next'
import Cookies from 'cookie-universal'
import { createHash, randomUUID } from 'crypto'
import type { IncomingMessage, ServerResponse } from 'http'
import type { Redis } from 'ioredis'
import { getDevAccessHeaders } from '@educator/lib/utils/devStagingAccessHeaders'
import { logger } from '@mc/pino-logger'
import {
  setAuthCookies,
  syncRequestAuthCookies,
  clearAuthCookies,
  COOKIE_NAMES,
} from './cookies'
import { redisClient } from './redisClient'

interface RefreshedTokens {
  access_token: string
  id_token?: string
  refresh_token?: string
  expires_in?: number
}

/**
 * Exchange a refresh token for a new token bundle upstream.
 *
 * Returns the parsed tokens on success, `null` when the upstream actively
 * rejects the token (non-2xx, e.g. `invalid_grant`), and propagates the
 * underlying fetch rejection on a transient timeout / network error. Does not
 * touch cookies — the caller owns cookie writes so that every concurrent caller
 * (including those served from the cross-pod cache below) writes to its own
 * response.
 */
const exchangeRefreshToken = async (
  refreshToken: string,
): Promise<RefreshedTokens | null> => {
  const response = await fetch(
    `${process.env.EDUCATOR_SERVER_URL}/oauth/token`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Forwarded-Proto': 'https', // Bypass Rails SSL redirect for internal service calls
        Accept: 'application/json',
        ...(getDevAccessHeaders() || {}),
      },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: process.env.EDUCATOR_AUTH_CLIENT_ID,
        client_secret: process.env.EDUCATOR_AUTH_CLIENT_SECRET || '',
        refresh_token: refreshToken,
      }),
    },
  )

  if (response.ok) {
    return (await response.json()) as RefreshedTokens
  }

  logger.error({
    msg: 'Token refresh failed',
    status: response.status,
    statusText: response.statusText,
  })
  return null
}

/**
 * Cross-pod refresh coordination.
 *
 * jarvis-vision runs many pods (HPA: 10–20). Refresh tokens rotate — each
 * successful `/oauth/token` exchange consumes the token it was given and issues
 * a new one — and Auth0 reuse-detection revokes the whole token family if a
 * consumed token is presented again. With every browser API call routed through
 * `[...proxy]` on an arbitrary pod, a burst of calls after the ~60s access
 * token expires would each redeem the SAME refresh token on a different pod →
 * reuse → family revoked → the user is logged out mid-session at random.
 *
 * A per-process guard cannot fix this (the previous in-memory single-flight was
 * reverted in #64161). The coordination instead lives in Redis, shared by all
 * pods, keyed by a hash of the refresh-token value:
 *
 *   1. A short-lived lock ensures only ONE pod calls `/oauth/token` for a given
 *      refresh token at a time; the rest become followers.
 *   2. The successful result is cached briefly under the hash of the *consumed*
 *      refresh token. Followers — and any straggler request on any pod that
 *      still carries the now-rotated token because the browser hasn't received
 *      the new cookie yet — read the rotated tokens from the cache instead of
 *      re-redeeming the consumed token.
 *
 * Everything fails open: if Redis is unavailable at any step, callers fall back
 * to a direct exchange, i.e. exactly the pre-coordination behaviour. A Redis
 * outage must never become an auth outage.
 */
const LOCK_KEY_PREFIX = 'educator:token:refresh:lock:'
const RESULT_KEY_PREFIX = 'educator:token:refresh:result:'

// Lock auto-expires on this TTL so a crashed leader can never wedge refreshes
// for a token permanently — a later request just re-acquires and retries.
const LOCK_TTL_MS = 12_000
// Grace window for stragglers/followers to read the rotated tokens. Kept well
// under the access-token lifetime so a cached token handed to a late caller is
// still usable, and short enough to bound how long a secret lives in Redis.
const RESULT_TTL_MS = 30_000
// A slow local render the browser abandons drops the rotated cookies, leaving
// it holding a consumed token; this cache is the only way back to the session.
const DEV_RESULT_TTL_MS = 7 * 24 * 60 * 60 * 1000
// A follower waits a little under the lock TTL for the leader to publish, then
// gives up and falls open to its own exchange.
const FOLLOWER_MAX_WAIT_MS = 11_000
const POLL_INTERVAL_MS = 100

// Refresh tokens are secrets; never use the raw value as a (loggable, possibly
// shared-cluster) Redis key. A hash is a stable, opaque identifier.
const hashToken = (refreshToken: string): string =>
  createHash('sha256').update(refreshToken).digest('hex')

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

// Release the lock only if we still own it, so a lock that already expired and
// was re-acquired by another pod is not deleted out from under it.
const RELEASE_LOCK_SCRIPT = `
  if redis.call("get", KEYS[1]) == ARGV[1] then
    return redis.call("del", KEYS[1])
  end
  return 0
`

const readCachedResult = async (
  redis: Redis,
  resultKey: string,
): Promise<RefreshedTokens | null> => {
  try {
    const cached = await redis.get(resultKey)
    return cached ? (JSON.parse(cached) as RefreshedTokens) : null
  } catch {
    // Miss, parse error, or Redis unavailable — caller proceeds without it.
    return null
  }
}

const cacheResult = async (
  redis: Redis,
  resultKey: string,
  tokens: RefreshedTokens,
): Promise<void> => {
  const ttlMs =
    process.env.NODE_ENV === 'development' ? DEV_RESULT_TTL_MS : RESULT_TTL_MS
  try {
    await redis.set(resultKey, JSON.stringify(tokens), 'PX', ttlMs)
  } catch {
    // Best-effort: a failed cache write just means stragglers re-exchange.
  }
}

type LockState = 'acquired' | 'held-by-other' | 'unavailable'

const tryAcquireLock = async (
  redis: Redis,
  lockKey: string,
  lockId: string,
): Promise<LockState> => {
  try {
    const result = await redis.set(lockKey, lockId, 'PX', LOCK_TTL_MS, 'NX')
    return result === 'OK' ? 'acquired' : 'held-by-other'
  } catch {
    return 'unavailable'
  }
}

const releaseLock = async (
  redis: Redis,
  lockKey: string,
  lockId: string,
): Promise<void> => {
  try {
    await redis.eval(RELEASE_LOCK_SCRIPT, 1, lockKey, lockId)
  } catch {
    // The lock auto-expires via its TTL; a failed release is harmless.
  }
}

const pollForResult = async (
  redis: Redis,
  resultKey: string,
): Promise<RefreshedTokens | null> => {
  const deadline = Date.now() + FOLLOWER_MAX_WAIT_MS
  while (Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    const cached = await readCachedResult(redis, resultKey)
    if (cached) {
      return cached
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_INTERVAL_MS)
  }
  return null
}

// Treat tokens within this many seconds of expiry as already expired so the
// proxy refreshes them before forwarding upstream. The educator access token
// has a 60s lifetime, and without this buffer a token with 0–2s remaining can
// be forwarded and expire mid-flight on the backend → 401.
const CLOCK_SKEW_BUFFER_S = 10

/**
 * Check if a JWT access token is expired.
 * Returns true if the token is expired or cannot be decoded.
 */
const isTokenExpired = (accessToken: string) => {
  try {
    const tokenPayload = JSON.parse(
      Buffer.from(accessToken.split('.')[1], 'base64').toString(),
    )
    const expiresAt = tokenPayload.exp
    const currentTime = Math.floor(Date.now() / 1000)
    // Per RFC 7519, the JWT must not be accepted at or after `exp`, so use <=.
    return expiresAt <= currentTime + CLOCK_SKEW_BUFFER_S
  } catch {
    return true
  }
}

const coordinatedExchange = async (
  refreshToken: string,
): Promise<RefreshedTokens | null> => {
  // Fail open: no Redis → behave exactly as the un-coordinated path.
  if (!redisClient) {
    return exchangeRefreshToken(refreshToken)
  }

  const key = hashToken(refreshToken)
  const resultKey = `${RESULT_KEY_PREFIX}${key}`
  const lockKey = `${LOCK_KEY_PREFIX}${key}`

  const fromCache = (
    cached: RefreshedTokens,
  ): RefreshedTokens | Promise<RefreshedTokens | null> =>
    cached.refresh_token &&
    cached.refresh_token !== refreshToken &&
    isTokenExpired(cached.access_token)
      ? coordinatedExchange(cached.refresh_token)
      : cached

  // 1. Another caller may have already rotated this token moments ago.
  const cached = await readCachedResult(redisClient, resultKey)
  if (cached) {
    return fromCache(cached)
  }

  // 2. Try to become the single pod that redeems this refresh token.
  const lockId = randomUUID()
  const lockState = await tryAcquireLock(redisClient, lockKey, lockId)

  if (lockState === 'unavailable') {
    return exchangeRefreshToken(refreshToken)
  }

  if (lockState === 'acquired') {
    try {
      // A different pod may have published a result between steps 1 and 2.
      const raced = await readCachedResult(redisClient, resultKey)
      if (raced) {
        return await fromCache(raced)
      }

      // May throw on a transient/network failure — propagate so the caller
      // clears cookies (clean logout) rather than leaving a half-auth state.
      const tokens = await exchangeRefreshToken(refreshToken)
      if (tokens) {
        await cacheResult(redisClient, resultKey, tokens)
      }
      return tokens
    } finally {
      await releaseLock(redisClient, lockKey, lockId)
    }
  }

  // 3. Follower: wait for the lock holder to publish the rotated tokens.
  const polled = await pollForResult(redisClient, resultKey)
  if (polled) {
    return fromCache(polled)
  }

  // 4. Leader never published (rejected / slow / crashed). Fail open.
  return exchangeRefreshToken(refreshToken)
}

/**
 * Get a new access token using the refresh token and write the resulting tokens
 * to this response's cookies. Returns the new access token on success, or null
 * when the refresh was rejected upstream. Propagates the underlying rejection on
 * a transient failure so the caller can clear the session.
 */
const refreshAccessToken = async ({
  req,
  res,
  refreshToken,
}: {
  req: IncomingMessage
  res: ServerResponse
  refreshToken: string
}): Promise<string | null> => {
  const refreshedTokens = await coordinatedExchange(refreshToken)

  if (!refreshedTokens) {
    return null
  }

  const newRefreshToken = refreshedTokens.refresh_token || refreshToken

  // Update cookies with new tokens
  setAuthCookies({
    res: res as NextApiResponse,
    accessToken: refreshedTokens.access_token,
    idToken: refreshedTokens.id_token,
    refreshToken: newRefreshToken,
    expiresIn: refreshedTokens.expires_in,
  })

  // Keep req coherent with the new cookies so same-request readers (e.g.
  // GrowthBook attribute lookup via getMcUserIdFromCtx) see the fresh token
  // rather than re-reading the expired one we just rotated out.
  syncRequestAuthCookies({
    req,
    accessToken: refreshedTokens.access_token,
    idToken: refreshedTokens.id_token,
    refreshToken: newRefreshToken,
  })

  return refreshedTokens.access_token
}

/**
 * Force a token refresh even though the current access token is still valid.
 *
 * Educator access is authorized by a JWT claim stamped at mint time
 * (Educator::AccessClaimsService on the backend), so a user granted access
 * AFTER their token was minted holds a valid-but-stale token that 403s on
 * every educator endpoint until it expires. Redeeming the refresh token
 * re-mints the claims from current DB state, picking the grant up
 * immediately. Goes through the same Redis-coordinated exchange as expiry
 * refreshes so concurrent callers across pods don't burn the rotating
 * refresh token.
 *
 * Never clears cookies: the caller has a working (unexpired) session and a
 * failed opportunistic refresh must not log them out — on any failure the
 * caller just proceeds with the original response.
 */
export const forceRefreshAccessToken = async ({
  req,
  res,
}: {
  req: NextApiRequest | GetServerSidePropsContext['req']
  res: NextApiResponse | GetServerSidePropsContext['res']
}): Promise<string | null> => {
  const cookies = Cookies(req, res)
  const refreshToken = cookies.get(COOKIE_NAMES.REFRESH_TOKEN)

  if (!refreshToken) {
    return null
  }

  try {
    return await refreshAccessToken({ req, res, refreshToken })
  } catch (error) {
    logger.error({ msg: 'Error force-refreshing access token', err: error })
    return null
  }
}

// Outside development a thrown refresh must clear cookies: keeping them with no
// usable access token loops users between auth guards and the login page.
const clearAuthCookiesAfterThrownRefresh = (res: ServerResponse) => {
  if (process.env.NODE_ENV === 'development') {
    return
  }
  clearAuthCookies(res as NextApiResponse)
}

/**
 * Get or refresh access token from cookies.
 *
 * Handles two expiry scenarios:
 * 1. Access token cookie expired (browser removed it) but refresh token still valid
 * 2. Access token cookie exists but the JWT inside it is expired
 *
 * In both cases, attempts to use the refresh token to get a new access token.
 */
export const getOrRefreshAccessToken = async ({
  req,
  res,
}: {
  req: IncomingMessage
  res: ServerResponse
}): Promise<string | null> => {
  const cookies = Cookies(req, res)
  const accessToken = cookies.get(COOKIE_NAMES.ACCESS_TOKEN)
  const refreshToken = cookies.get(COOKIE_NAMES.REFRESH_TOKEN)

  // No access token cookie — it may have expired while the JWT (and refresh token) are still valid.
  // Attempt to refresh if we have a refresh token, otherwise clear everything.
  if (!accessToken) {
    if (!refreshToken) {
      return null
    }

    try {
      return await refreshAccessToken({ req, res, refreshToken })
    } catch (error) {
      logger.error({ msg: 'Error refreshing expired access token', err: error })
      clearAuthCookiesAfterThrownRefresh(res)
      return null
    }
  }

  // Access token cookie exists — check if the JWT inside is expired
  if (isTokenExpired(accessToken)) {
    if (!refreshToken) {
      clearAuthCookies(res as NextApiResponse)
      return null
    }

    try {
      return await refreshAccessToken({ req, res, refreshToken })
    } catch (error) {
      logger.error({ msg: 'Error refreshing expired JWT', err: error })
      clearAuthCookiesAfterThrownRefresh(res)
      return null
    }
  }

  return accessToken
}
